import { useState, useCallback } from 'react';
import { 
  getPropertyDataFromCoordinates,
  createFavoriteLandFeature,
  changeFlashLightFeature,
  deletePoint,
  getCitiesOfRegion,
  getDistrictOfCity,
  trackingMyLocationFunction,
  generateLogos,
  generateBuildingsWalletLogos,
  removeLogos,
  removeLogoGraphics,
  animationEffect,
  clearAnimationIntervals,
  addStop as routeAddStop,
  showRoute as routeShowRoute,
  clearRoute as routeClearRoute,
  getRouteParams as routeGetRouteParams,
  getRouteLayer as routeGetRouteLayer,
  stubGeolocation,
  createFeatureGraphics,
  getPolygonCenterPoint,
  getIntersectWithGraphics,
  callBackWatchPosition,
  getRegionNameByGis,
  getGraphicFromObj,
  getPlans,
  getParcels,
} from '../utils';
import { 
  getCurrentPosition,
  checkLocationPermissions,
  clearLocationWatch,
  getTrackingState,
  setTrackingState
} from '../utils';

export const useMapFunctions = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Property functions
  const handleZoomToLayer = useCallback((type: any, geometry: any, wiki: any, ZoomLevel: any, graphicsLayer: any, MapViewData: any) => {
    // This function is now handled directly in useMapLogic
    console.warn('handleZoomToLayer is deprecated, use useMapLogic.zoomToLayer instead');
  }, []);

  const handleGetPropertyData = useCallback(async (longitude: number, latitude: number) => {
    try {
      setIsLoading(true);
      const result = await getPropertyDataFromCoordinates(longitude, latitude);
      return result;
    } catch (error) {
      console.error('Error getting property data:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCreateFavoriteLand = useCallback(async (
    property: any,
    parcel_Cod: string,
    city_Name: string,
    districts_Name: string,
    neighbourhood_Name: string,
    propertyNumber: string,
    pPointgraphic: any,
    F_FavoriteLand: any
  ) => {
    try {
      setIsLoading(true);
      const result = await createFavoriteLandFeature(
        property,
        parcel_Cod,
        city_Name,
        districts_Name,
        neighbourhood_Name,
        propertyNumber,
        pPointgraphic,
        F_FavoriteLand
      );
      return result;
    } catch (error) {
      console.error('Error creating favorite land:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleChangeFlashLight = useCallback(async (
    property: any,
    selectedFeature: any,
    currentSelectedFeature: any,
    F_Land_parcels: any
  ) => {
    try {
      setIsLoading(true);
      const result = await changeFlashLightFeature(
        property,
        selectedFeature,
        currentSelectedFeature,
        F_Land_parcels
      );
      return result;
    } catch (error) {
      console.error('Error changing flashlight feature:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleDeletePoint = useCallback(async (objId: any, F_FavoriteLand: any) => {
    try {
      const result = await deletePoint(objId, F_FavoriteLand);
      return result;
    } catch (error) {
      console.error('Error deleting point:', error);
      throw error;
    }
  }, []);

  // Routing functions
  const handleAddStop = useCallback(async (eventOrGraphic: any, mapViewData?: any) => {
    await routeAddStop(eventOrGraphic, mapViewData);
  }, []);

  const handleShowRoute = useCallback((data: any) => {
    return routeShowRoute(data);
  }, []);

  const handleClearRoute = useCallback((localRouteParams?: any, localRouteLayer?: any) => {
    routeClearRoute();
    // Also clear the route layer directly to ensure it's removed from the map
    const routeLayer = routeGetRouteLayer();
    if (routeLayer) {
      routeLayer.removeAll();
    }
    // Clear local route layer if provided
    if (localRouteLayer) {
      localRouteLayer.removeAll();
    }
    // Clear local routeParams if provided
    if (localRouteParams && localRouteParams.stops && localRouteParams.stops.features) {
      localRouteParams.stops.features = [];
    }
  }, []);

  const handleGetRouteParams = useCallback(() => {
    return routeGetRouteParams();
  }, []);

  const handleGetRouteLayer = useCallback(() => {
    return routeGetRouteLayer();
  }, []);

  // Search functions
  const handleGetCities = useCallback(async (
    selectedRegion: string,
    setIsLoading: (loading: boolean) => void,
    setCities: (cities: any[]) => void,
    presentToastFunction: any,
    presentToast: any
      ) => {
    await getCitiesOfRegion(selectedRegion, setIsLoading, setCities, presentToastFunction, presentToast);
  }, []);

  const handleGetDistricts = useCallback(async (
    selectedRegion: string,
    selectedCity: string,
    setIsLoading: (loading: boolean) => void,
    setDistricts: any,
    presentToastFunction: any,
    presentToast: any
  ) => {
    await getDistrictOfCity(selectedRegion, selectedCity, setIsLoading, setDistricts, presentToastFunction, presentToast);
  }, []);

  const handleGetPlans = useCallback(async (
    selectedRegion: string,
    selectedCity: string,
    selectedDistrict: string,
    setIsLoading: (loading: boolean) => void,
    setParcels: (parcels: any[]) => void,
    presentToastFunction: any,
    presentToast: any
  ) => {
    await getPlans( 
      selectedRegion,
      selectedCity,
      selectedDistrict,
      setIsLoading,
      setParcels,
      presentToastFunction,
      presentToast
    );
  }, []);

  const handleGetParcels = useCallback(async (
    selectedRegion: string,
    selectedCity: string,
    selectedDistrict: string,  
    planNo: string,
    setIsLoading: (loading: boolean) => void,
    setParcels: (parcels: any[]) => void,
    presentToastFunction: any,
    presentToast: any
  ) => {
    await getParcels( 
      selectedRegion,
      selectedCity,
      selectedDistrict,
      planNo,
      setIsLoading,
      setParcels,
      presentToastFunction,
      presentToast
    );
  }, []);

  const handleGetGraphicFromObj = useCallback(async (
    obj: any,
    zoomToLayer: any,
    presentToastFunction: any,
    presentToast: any
  ) => {
    await getGraphicFromObj(obj, setIsLoading, zoomToLayer, presentToastFunction, presentToast);
  }, []);

  // Location functions
  const handleTrackingLocation = useCallback(async (
    isNotClear: boolean,
    graphicsLayer?: any,
    MapViewData?: any,
    showToastCallback?: (position: "top" | "middle" | "bottom", color: string, message: string) => void
  ) => {
    await trackingMyLocationFunction(isNotClear, graphicsLayer, MapViewData, showToastCallback);
  }, []);

  const handleGetCurrentPosition = useCallback(async () => {
    try {
      const result = await getCurrentPosition();
      return result;
    } catch (error) {
      console.error('Error getting current position:', error);
      throw error;
    }
  }, []);

  const handleCheckLocationPermissions = useCallback(async () => {
    return await checkLocationPermissions();
  }, []);

  const handleClearLocationWatch = useCallback(async () => {
    await clearLocationWatch();
  }, []);

  const handleGetTrackingState = useCallback(() => {
    return getTrackingState();
  }, []);

  const handleSetTrackingState = useCallback((zoomTracking: boolean, trackCurrentLocation: boolean) => {
    setTrackingState(zoomTracking, trackCurrentLocation);
  }, []);

  // Logo functions
  const handleGenerateLogos = useCallback((
    type: any,
    id: any,
    file: any,
    longitude: any,
    latitude: any,
    width: any,
    height: any,
    graphicsLogoLayer: any,
    graphicsLogoLayerlogoAdd: any,
    MapViewData?: any
  ) => {
    generateLogos(
      type,
      id,
      file,
      longitude,
      latitude,
      width,
      height,
      graphicsLogoLayer,
      graphicsLogoLayerlogoAdd,
      MapViewData
    );
  }, []);

  const handleGenerateBuildingsWalletLogos = useCallback((
    id: any,
    longitude: any,
    latitude: any,
    parcel_Cod: any,
    land_Numbe: any,
    title: any,
    graphicsbuildingsWalletLayer: any
  ) => {
    generateBuildingsWalletLogos(
      id,
      longitude,
      latitude,
      parcel_Cod,
      land_Numbe,
      title,
      graphicsbuildingsWalletLayer
    );
  }, []);

  const handleRemoveLogos = useCallback((
    id: any,
    getLogosData: any[],
    setGetLogosData: any
  ) => {
    removeLogos(id, getLogosData, setGetLogosData);
  }, []);

  const handleRemoveLogoGraphics = useCallback((
    logoId: string,
    graphicsLogoLayer: any,
    graphicsLogoLayerlogoAdd: any
  ) => {
    removeLogoGraphics(logoId, graphicsLogoLayer, graphicsLogoLayerlogoAdd);
  }, []);

  // Animation functions
  const handleAnimationEffect = useCallback((mapView: any) => {
    animationEffect(mapView);
  }, []);

  const handleClearAnimationIntervals = useCallback(() => {
    clearAnimationIntervals();
  }, []);

  // Utility functions
  const handleStubGeolocation = useCallback(() => {
    return stubGeolocation();
  }, []);

  const handleCreateFeatureGraphics = useCallback((
    type: any,
    geometry: any,
    wkid: any,
    attributes: any,
    symbol: any
  ) => {
    return createFeatureGraphics(type, geometry, wkid, attributes, symbol);
  }, []);

  const handleGetPolygonCenterPoint = useCallback((polygonGeometry: any) => {
    return getPolygonCenterPoint(polygonGeometry);
  }, []);

  const handleGetIntersectWithGraphics = useCallback(async (graphic: any, layer: any) => {
    return await getIntersectWithGraphics(graphic, layer);
  }, []);

  const handleCallBackWatchPosition = useCallback((
    position: any,
    err: any,
    graphicsLayer: any,
    MapViewData: any
  ) => {
    return callBackWatchPosition(position, err, graphicsLayer, MapViewData);
  }, []);

  const handleGetRegionNameByGis = useCallback(async (MapViewData: any, setCurrentRegion: any) => {
    return await getRegionNameByGis(MapViewData, setCurrentRegion);
  }, []);

  return {
    isLoading,
    setIsLoading,
    // Property functions
    handleGetPropertyData,
    handleCreateFavoriteLand,
    handleChangeFlashLight,
    handleDeletePoint,
    // Routing
    handleAddStop,
    handleShowRoute,
    handleClearRoute,
    handleGetRouteParams,
    handleGetRouteLayer,
    // Search functions
    handleGetCities,
    handleGetDistricts,
    handleGetPlans,
    handleGetParcels,
    handleGetGraphicFromObj,
    // Location functions
    handleTrackingLocation,
    handleGetCurrentPosition,
    handleCheckLocationPermissions,
    handleClearLocationWatch,
    handleGetTrackingState,
    handleSetTrackingState,
    // Logo functions
    handleGenerateLogos,
    handleGenerateBuildingsWalletLogos,
    handleRemoveLogos,
    handleRemoveLogoGraphics,
    // Animation functions
    handleAnimationEffect,
    handleClearAnimationIntervals,
    // Utility functions
    handleStubGeolocation,
    handleCreateFeatureGraphics,
    handleGetPolygonCenterPoint,
    handleGetIntersectWithGraphics,
    handleCallBackWatchPosition,
    handleGetRegionNameByGis,
  };
};
